import React, { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, fmtDate } from '@/lib/api';
import { R } from '@/lib/routes';
import { ENGAGE } from '@/constants/testIds';
import { cn } from '@/lib/utils';

export const NotificationBell = ({ module = 'crm', onNavigate }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () =>
    api.get(`/notifications?module=${module}`).then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [module]); // eslint-disable-line
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch {} load(); }
    if (onNavigate) { onNavigate(n); return; }
    if (n.kind === 'eoi') { navigate(R.eoiHub); return; }
    if (n.kind === 'reminder') { navigate(R.reminders); return; }
    if (n.kind === 'admin_ops') { navigate(R.adminOps); return; }
    if (!n.booking_id) { navigate(R.reminders); return; }
    const cq = n.comment_id ? `&c=${n.comment_id}` : '';
    navigate(`${R.booking(n.booking_id)}?tab=${n.section || 'overview'}${cq}`);
  };
  const readAll = async () => { try { await api.post('/notifications/read-all'); } catch {} load(); };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={ENGAGE.bell}
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-xl p-2.5 text-stone-500 transition hover:bg-stone-100 hover:text-stone-800"
        title="Notifications"
      >
        <Bell className="h-5 w-5" strokeWidth={2.2} />
        {unread > 0 && (
          <span
            data-testid={ENGAGE.badge}
            className={cn('absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white', module === 'admin_ops' ? 'bg-[#1565C0]' : 'bg-[#245329]')}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] max-w-[90vw] overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-stone-100 px-4 py-3">
            <span className="font-head text-sm font-bold text-stone-800">Notifications</span>
            {unread > 0 && (
              <button
                data-testid="notif-read-all"
                onClick={readAll}
                className={cn('flex items-center gap-1 text-xs font-semibold', module === 'admin_ops' ? 'text-[#1565C0] hover:text-[#0d4a94]' : 'text-[#245329] hover:text-[#1c4220]')}
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-stone-400">You're all caught up.</p>}
            {items.map((n) => (
              <button
                key={n.id}
                data-testid={ENGAGE.item}
                onClick={() => openItem(n)}
                className={cn(
                  'flex w-full flex-col items-start gap-1 border-b border-stone-50 px-4 py-3 text-left transition',
                  module === 'admin_ops' ? 'hover:bg-blue-50/50' : 'hover:bg-[#E6F4EA]/40',
                  !n.read && (module === 'admin_ops' ? 'bg-blue-50/60' : 'bg-[#E6F4EA]/60')
                )}
              >
                <span className="text-sm text-stone-700">{n.text}</span>
                <span className="text-xs text-stone-400">{[n.booking_code || n.label, fmtDate(n.created_at)].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
